import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createFilteredUrl,
  isTileClickable,
  getTileTooltip,
  TileDataWithDrillDown,
} from '../../../utils/urlUtils';

interface PackerStat {
  packerId: string | null;
  packerName: string;
  orderCount: number;
}

interface PackingStatsData {
  ordersBeingPackedCount: number | null;
  ordersBeingProcessedCount: number | null;
  ordersBeingPackedCountLastSync: string | null;
  totalOrdersPackedToday: number;
  packedByPacker: PackerStat[];
}

interface PackingStatsTileProps {
  data: TileDataWithDrillDown & {
    status?: string;
    error?: string;
    data?: PackingStatsData;
  };
  targetUrl?: string;
}

export const PackingStatsTile: React.FC<PackingStatsTileProps> = ({ data, targetUrl }) => {
  const navigate = useNavigate();

  if (data.status === 'error') {
    return (
      <div className="h-full flex items-center justify-center text-center">
        <p className="text-red-600 dark:text-red-400 text-sm">{data.error || 'Chyba při načítání dat'}</p>
      </div>
    );
  }

  const stats = data.data;
  if (!stats) return null;

  const isClickable = isTileClickable(data);
  const tooltip = getTileTooltip(data);

  const handleClick = () => {
    if (isClickable && targetUrl && data.drillDown?.filters) {
      navigate(createFilteredUrl(targetUrl, data.drillDown.filters));
    }
  };

  return (
    <div
      className={`h-full flex flex-col gap-3 ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      title={tooltip}
    >
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-secondary-blue-pale dark:bg-graphite-surface-2 rounded-lg p-3 text-center">
          <p className="text-xs text-neutral-gray dark:text-graphite-muted mb-1">Vyřizuje se</p>
          <p className="text-2xl font-bold text-primary-blue dark:text-graphite-accent">
            {stats.ordersBeingProcessedCount ?? '—'}
          </p>
          {stats.ordersBeingPackedCountLastSync && (
            <p className="text-xs text-neutral-gray dark:text-graphite-muted mt-1">
              sync {new Date(stats.ordersBeingPackedCountLastSync).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div className="bg-secondary-blue-pale dark:bg-graphite-surface-2 rounded-lg p-3 text-center">
          <p className="text-xs text-neutral-gray dark:text-graphite-muted mb-1">Balí se</p>
          <p className="text-2xl font-bold text-primary-blue dark:text-graphite-accent">
            {stats.ordersBeingPackedCount ?? '—'}
          </p>
          {stats.ordersBeingPackedCountLastSync && (
            <p className="text-xs text-neutral-gray dark:text-graphite-muted mt-1">
              sync {new Date(stats.ordersBeingPackedCountLastSync).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div className="bg-secondary-blue-pale dark:bg-graphite-surface-2 rounded-lg p-3 text-center">
          <p className="text-xs text-neutral-gray dark:text-graphite-muted mb-1">Zabaleno dnes</p>
          <p className="text-2xl font-bold text-primary-blue dark:text-graphite-accent">
            {stats.totalOrdersPackedToday}
          </p>
        </div>
      </div>

      {stats.packedByPacker.length > 0 && (
        <ul className="space-y-1 flex-1 overflow-y-auto">
          {stats.packedByPacker.map((p) => (
            <li
              key={p.packerId ?? p.packerName}
              className="flex items-center justify-between py-1.5 px-2 bg-secondary-blue-pale dark:bg-graphite-surface-2 rounded"
            >
              <span className="text-xs font-medium text-neutral-slate dark:text-graphite-text truncate">{p.packerName}</span>
              <span className="text-xs font-bold text-primary-blue dark:text-graphite-accent ml-2 flex-shrink-0">{p.orderCount}</span>
            </li>
          ))}
        </ul>
      )}

      {stats.packedByPacker.length === 0 && (
        <p className="text-xs text-neutral-gray dark:text-graphite-muted italic">Dnes zatím nikdo nezabalil žádnou objednávku.</p>
      )}
    </div>
  );
};
